import React, { useState, useEffect, useCallback } from "react";
import { supabase } from "../supabaseClient";
import { COLORS } from "../config/themes";

// ─── helpers ────────────────────────────────────────────────────────────────
const todayStr = () => new Date().toISOString().split("T")[0];
const nowStr   = () => new Date().toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });

// ─── sub-components ─────────────────────────────────────────────────────────

const PinDot = ({ filled }) => (
  <div style={{
    width: 18, height: 18, borderRadius: "50%",
    background: filled ? "#fff" : "transparent",
    border: "2px solid rgba(255,255,255,0.6)",
    transition: "background 0.15s",
  }} />
);

const PadBtn = ({ label, sub, onClick, danger }) => (
  <button
    onClick={onClick}
    style={{
      background: danger ? "rgba(239,68,68,0.25)" : "rgba(255,255,255,0.12)",
      border: "1.5px solid rgba(255,255,255,0.18)",
      borderRadius: 18,
      color: "#fff",
      fontSize: 26,
      fontWeight: 600,
      fontFamily: "inherit",
      cursor: "pointer",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      padding: "18px 0",
      gap: 2,
      transition: "background 0.1s, transform 0.08s",
      userSelect: "none",
      WebkitTapHighlightColor: "transparent",
    }}
    onMouseDown={e => e.currentTarget.style.transform = "scale(0.94)"}
    onMouseUp={e => e.currentTarget.style.transform = "scale(1)"}
    onTouchStart={e => e.currentTarget.style.transform = "scale(0.94)"}
    onTouchEnd={e => e.currentTarget.style.transform = "scale(1)"}
  >
    <span>{label}</span>
    {sub && <span style={{ fontSize: 9, fontWeight: 500, letterSpacing: 1, opacity: 0.6 }}>{sub}</span>}
  </button>
);

// ─── confirmation screen ─────────────────────────────────────────────────────
const ConfirmScreen = ({ student, direction, time, onDone }) => {
  useEffect(() => {
    const t = setTimeout(onDone, 4000);
    return () => clearTimeout(t);
  }, [onDone]);

  const initials = `${student.firstName?.[0] || ""}${student.lastName?.[0] || ""}`.toUpperCase();
  const isIn = direction === "in";

  return (
    <div style={{
      minHeight: "100vh", display: "flex", flexDirection: "column",
      alignItems: "center", justifyContent: "center",
      background: isIn
        ? "linear-gradient(135deg, #065f46 0%, #047857 50%, #059669 100%)"
        : "linear-gradient(135deg, #1e3a5f 0%, #1d4ed8 50%, #3b82f6 100%)",
      fontFamily: "'Nunito', sans-serif",
      color: "#fff",
      textAlign: "center",
      padding: 40,
    }}>
      <div style={{
        width: 100, height: 100, borderRadius: "50%",
        background: "rgba(255,255,255,0.2)",
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: 36, fontWeight: 800, marginBottom: 28,
        border: "3px solid rgba(255,255,255,0.4)",
      }}>
        {initials}
      </div>

      <div style={{ fontSize: 54, marginBottom: 12 }}>
        {isIn ? "✓" : "👋"}
      </div>

      <h1 style={{ fontSize: 40, fontWeight: 900, margin: "0 0 8px" }}>
        {student.firstName} {student.lastName}
      </h1>

      <div style={{
        fontSize: 22, fontWeight: 700, opacity: 0.9, marginBottom: 8,
        background: "rgba(255,255,255,0.15)", borderRadius: 12,
        padding: "10px 28px", display: "inline-block",
      }}>
        {isIn ? "Checked In" : "Checked Out"} · {time}
      </div>

      <p style={{ fontSize: 16, opacity: 0.7, marginTop: 32 }}>
        Have a great {isIn ? "day" : "evening"}! Returning in a moment…
      </p>

      <div style={{
        marginTop: 24, width: 200, height: 4, borderRadius: 2,
        background: "rgba(255,255,255,0.2)", overflow: "hidden",
      }}>
        <div style={{
          height: "100%", borderRadius: 2,
          background: "rgba(255,255,255,0.7)",
          animation: "shrink 4s linear forwards",
        }} />
      </div>

      <style>{`
        @keyframes shrink { from { width: 100%; } to { width: 0%; } }
      `}</style>
    </div>
  );
};

// ─── main kiosk ──────────────────────────────────────────────────────────────
const KioskPage = ({ schoolId, students, onExit }) => {
  const [pin, setPin]           = useState("");
  const [error, setError]       = useState("");
  const [loading, setLoading]   = useState(false);
  const [confirmed, setConfirmed] = useState(null); // { student, direction, time }
  const [clock, setClock]       = useState(nowStr());
  const today = todayStr();

  // live clock
  useEffect(() => {
    const t = setInterval(() => setClock(nowStr()), 10000);
    return () => clearInterval(t);
  }, []);

  // keyboard support
  useEffect(() => {
    const handler = (e) => {
      if (confirmed) return;
      if (e.key >= "0" && e.key <= "9") handleDigit(e.key);
      if (e.key === "Backspace") handleDelete();
      if (e.key === "Enter" && pin.length === 4) handleSubmit();
      if (e.key === "Escape" && onExit) onExit();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [pin, confirmed]);

  const handleDigit = useCallback((d) => {
    setError("");
    setPin(prev => prev.length < 4 ? prev + d : prev);
  }, []);

  const handleDelete = useCallback(() => {
    setError("");
    setPin(prev => prev.slice(0, -1));
  }, []);

  const handleSubmit = useCallback(async () => {
    if (pin.length !== 4) return;
    setLoading(true);
    setError("");

    try {
      // 1. Find parent profile by PIN
      const { data: profiles, error: pErr } = await supabase
        .from("profiles")
        .select("id, name, checkin_pin, school_id")
        .eq("school_id", schoolId)
        .eq("checkin_pin", pin)
        .eq("role", "parent");

      if (pErr || !profiles?.length) {
        setError("Incorrect PIN. Please try again.");
        setPin("");
        setLoading(false);
        return;
      }

      const parentProfile = profiles[0];

      // 2. Find linked students for this parent
      const { data: parentRecord } = await supabase
        .from("profiles")
        .select("linked_students")
        .eq("id", parentProfile.id)
        .single();

      const linkedIds = parentRecord?.linked_students || [];

      if (!linkedIds.length) {
        setError("No students linked to this PIN.");
        setPin("");
        setLoading(false);
        return;
      }

      // For now handle single student (multi-student families handled in future)
      const student = students.find(s => linkedIds.includes(s.id));
      if (!student) {
        setError("Student not found. Contact your administrator.");
        setPin("");
        setLoading(false);
        return;
      }

      // 3. Determine direction: check last log for this student today
      const { data: lastLog } = await supabase
        .from("checkin_log")
        .select("direction")
        .eq("school_id", schoolId)
        .eq("student_id", student.id)
        .eq("date", today)
        .order("timestamp", { ascending: false })
        .limit(1);

      const lastDir = lastLog?.[0]?.direction;
      const direction = lastDir === "in" ? "out" : "in";
      const timestamp = new Date().toISOString();
      const time = nowStr();

      // 4. Insert check-in log
      await supabase.from("checkin_log").insert({
        school_id: schoolId,
        student_id: student.id,
        direction,
        method: "pin",
        checked_in_by: parentProfile.id,
        timestamp,
        date: today,
      });

      // 5. Update student attendance if checking in
      if (direction === "in") {
        await supabase.from("student_attendance").upsert({
          school_id: schoolId,
          student_id: student.id,
          date: today,
          status: "present",
        }, { onConflict: "school_id,student_id,date" });
      }

      setPin("");
      setConfirmed({ student, direction, time });
    } catch (e) {
      console.error("Kiosk error:", e);
      setError("Something went wrong. Please try again.");
      setPin("");
    }
    setLoading(false);
  }, [pin, schoolId, students, today]);

  // auto-submit when 4 digits entered
  useEffect(() => {
    if (pin.length === 4) handleSubmit();
  }, [pin]);

  if (confirmed) {
    return (
      <ConfirmScreen
        student={confirmed.student}
        direction={confirmed.direction}
        time={confirmed.time}
        onDone={() => setConfirmed(null)}
      />
    );
  }

  const PAD = [
    ["1","ABC"],["2","DEF"],["3","GHI"],
    ["4","JKL"],["5","MNO"],["6","PQR"],
    ["7","STU"],["8","VWX"],["9","XYZ"],
    [null,null],["0",""],["⌫",null],
  ];

  return (
    <div style={{
      minHeight: "100vh",
      background: "linear-gradient(160deg, #0f172a 0%, #1e3a5f 50%, #0f172a 100%)",
      display: "flex", flexDirection: "column",
      alignItems: "center", justifyContent: "center",
      fontFamily: "'Nunito', sans-serif",
      color: "#fff",
      padding: 24,
      userSelect: "none",
    }}>
      {/* exit button (for admin/teacher) */}
      {onExit && (
        <button onClick={onExit} style={{
          position: "fixed", top: 20, right: 20,
          background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.2)",
          borderRadius: 10, color: "rgba(255,255,255,0.6)", padding: "8px 16px",
          cursor: "pointer", fontSize: 13, fontFamily: "inherit", fontWeight: 600,
        }}>
          ✕ Exit Kiosk
        </button>
      )}

      {/* clock */}
      <div style={{ fontSize: 56, fontWeight: 300, letterSpacing: -2, opacity: 0.9, marginBottom: 4 }}>
        {clock}
      </div>
      <div style={{ fontSize: 16, opacity: 0.5, marginBottom: 40, letterSpacing: 1 }}>
        {new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}
      </div>

      {/* prompt */}
      <h2 style={{ fontSize: 22, fontWeight: 700, opacity: 0.9, margin: "0 0 28px", textAlign: "center" }}>
        Enter your 4-digit check-in PIN
      </h2>

      {/* dots */}
      <div style={{ display: "flex", gap: 20, marginBottom: 28 }}>
        {[0,1,2,3].map(i => <PinDot key={i} filled={i < pin.length} />)}
      </div>

      {/* error */}
      {error && (
        <div style={{
          background: "rgba(239,68,68,0.2)", border: "1px solid rgba(239,68,68,0.4)",
          borderRadius: 10, padding: "10px 20px", marginBottom: 20,
          fontSize: 14, fontWeight: 600, color: "#fca5a5", textAlign: "center",
        }}>
          {error}
        </div>
      )}

      {loading && (
        <div style={{ marginBottom: 20, fontSize: 14, opacity: 0.7 }}>Checking in…</div>
      )}

      {/* numpad */}
      <div style={{
        display: "grid", gridTemplateColumns: "repeat(3, 88px)",
        gap: 12, maxWidth: 300,
      }}>
        {PAD.map(([digit, sub], i) => {
          if (digit === null) return <div key={i} />;
          if (digit === "⌫") return (
            <PadBtn key={i} label="⌫" onClick={handleDelete} />
          );
          return (
            <PadBtn key={i} label={digit} sub={sub} onClick={() => handleDigit(digit)} />
          );
        })}
      </div>

      <p style={{ marginTop: 40, fontSize: 13, opacity: 0.4, textAlign: "center" }}>
        Don't have a PIN? Ask your school administrator.
      </p>
    </div>
  );
};

export default KioskPage;
